import React from 'react'
import { MessageData, toggleMessageDeletion } from '../message'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { Loader } from '../../../components/simple/loader'
import {
  IconArchive,
  IconCaretUp,
  IconEmail,
  IconEmailText,
  IconFlag,
  IconGear,
  IconInbox,
  IconLanguage,
  IconPhone,
  IconShare,
  IconSwap,
  IconText,
  IconVideo,
} from '../../../icons/library'
import { TextInput } from '../../../components/form/textInput'
import { Dropdown, DropdownItem } from '../../../components/simple/dropdown'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import { IconTranslatedCall } from '../../../icons/translatedCall'
import { AvatarInfo } from '../../../components/avatar'
import { Attachment } from '../../../components/media/attachment'
import { MessageTags, TagsInfo } from '../messageTags'
import { PhoneConversation } from './phoneConversation'
import { StandardConversation } from './standardConversation'
import { TextConversation } from './textConversation'
import { IconAnnouncementCall } from '../../../icons/announcementCall'
import { MessageInfoLauncher } from '../../modals/messageinfo/messageInfo'
import { MiniPieData } from '../../../components/charts/miniPie'
import { deltaToText } from '../../../components/quill/quillTextarea'
import { ConversationReply, DEFAULT_REPLY_STATE, ReplyState } from './conversationReply'
import { ConversationShare } from './conversationShare'
import { useTermsOfService } from '../../modals/termsOfService'
import { useAppContext, User } from '../../../providers/context'
import { useTranslation } from '../../../providers/translation'
import { AutoScrollToBottom } from '../../../layouts/components/scrollPreserve'
import { AttendanceConversation } from './attendanceConversation'
import { Popups } from '../../../providers/popups'
import { Toasts } from '../../../providers/toast'

export type ConversationType = MessageData['type']

export interface ReplyOption {
  name: string
  type: string
  priority: number
  recipientCount: number
  recipientNotify: number
  noConfirmReceipt: boolean
  alsoToTeachers: boolean
}

export interface ConversationMessageData {
  id: number
  avatar?: AvatarInfo
  sendersPerspective: boolean
  content: string
  attachments?: Attachment[]
  tags?: TagsInfo
  undeliveredInfo?: string
  sentAsEmail?: boolean
  sentAsWhatsApp?: boolean
  sentAsText?: boolean
  teacherOnly?: boolean
  date: string
  audioUrl?: string
  massRecordingURL?: string
  directReply?: ReplyOption
  archived?: boolean
  extraLabelForText?: string
  confirmReceiptPie?: MiniPieData
  canViewMessageInfo: boolean
}
interface BaseConversationData {
  type: ConversationType
  id: number
  massIconTypes?: ConversationType[]
  messageForTags: {
    avatar: AvatarInfo
    subject?: string
    tags: TagsInfo
    lk: boolean
    conference: boolean
  }
  canDelete?: boolean
  canReply?: boolean
  replyOptions?: ReplyOption[]
  canShare?: boolean
  hasTranslation?: boolean
  otherLanguageName: string
  deleted?: boolean
  archived?: boolean
  repliesToShared?: boolean
  isShared?: boolean
  alreadyShared?: string[]
  wasRead: boolean
}

export interface AttendanceConversationData extends BaseConversationData {
  type: 'attendance'
  canReply: undefined
  replyOptions: undefined
  canShare: undefined

  attendancePerClass: boolean
  schoolName: string
  date: string
  pusherChannel: string
  replyable: boolean
  blocks: {
    name: string
    messageID: number
  }[]
  attendancePie: MiniPieData
  totalAbsences: number
  absences: {
    id: number
    resolved: boolean
    student: {
      name: string
      id: number
    }
    repliedByGuardian?: {
      name: string
      comment?: string
      id: number
      attachmentURL?: string
    }
    excusedThroughSIS: boolean
    blocks: string[] // undefined implies general absence (reported per day)
  }[]
}

export interface PhoneConversationData extends BaseConversationData {
  type: 'phone'
  callAudioUrl: string
  lkMessageInfo: string
  callTranscript: { text: string; speaker: 'left' | 'right' }[]
  canReply: undefined
  canViewMessageInfo: boolean
}

export interface ThreadConversationData extends BaseConversationData {
  type: Exclude<ConversationType, 'attendance' | 'phone'>
  replyOptions: ReplyOption[]
  thread: ConversationMessageData[]
  canViewMessageInfo: boolean
}

export type ConversationData = AttendanceConversationData | ThreadConversationData | PhoneConversationData

const renderIcon = (iconType: ConversationType, data: ConversationData) =>
  iconType == 'phone' ? (
    data.messageForTags.lk ? (
      <IconTranslatedCall key={iconType} className="text-phone" size="xl" />
    ) : data.messageForTags.conference ? (
      <IconVideo key={iconType} className="text-phone" size="xl" />
    ) : (
      <IconPhone key={iconType} className="text-phone" size="xl" />
    )
  ) : iconType == 'email' ? (
    <IconEmail key={iconType} className="text-email" size="xl" />
  ) : iconType == 'text' ? (
    <IconText key={iconType} className="text-text" size="xl" />
  ) : iconType == 'both' ? (
    <IconEmailText key={iconType} className="text-both" size="xl" />
  ) : iconType == 'mass' ? (
    <IconAnnouncementCall key={iconType} className="text-both" size="xl" />
  ) : (
    <IconGear key={iconType} className="" size="xl" />
  )

const ConversationHeader: React.FC<{
  data: ThreadConversationData | PhoneConversationData
  translated: boolean
  setTranslated: (translated: boolean) => void
  reloadConversation: () => void
  user: User
}> = ({ data, translated, setTranslated, reloadConversation, user }) => {
  const iconTypes = data.type === 'mass' ? data.massIconTypes || [] : [data.type]
  const { tr } = useTranslation()
  return (
    <MessageInfoLauncher
      messageID={data.id}
      className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 p-4"
      canLaunch={data.canViewMessageInfo}
    >
      <div className="flex items-center gap-2 overflow-hidden text-nowrap -sm:ml-6">
        {data.type === 'mass' && <b>Broadcast Message</b>}
        {iconTypes.map((iconType) => renderIcon(iconType, data))}
        {data.messageForTags.subject && (
          <>
            {tr('Subject:')}{' '}
            <span className="overflow-hidden text-ellipsis font-bold">{data.messageForTags.subject}</span>
          </>
        )}
      </div>
      <MessageTags
        className="ml-auto"
        tags={data.messageForTags.tags}
        fixedSize
        extras={
          <>
            {data.hasTranslation &&
              (user.type === 'teacher' ? (
                <IconLanguage
                  onClick={(e) => {
                    e.stopPropagation()
                    setTranslated(!translated)
                  }}
                  className="cursor-pointer hover:text-logo"
                  tooltip="Swap message language"
                />
              ) : (
                <Button
                  buttonStyle={ButtonStyle.warning}
                  small
                  onClick={(e) => {
                    e.stopPropagation()
                    setTranslated(!translated)
                  }}
                >
                  <IconSwap />
                  {data.otherLanguageName}
                </Button>
              ))}
            {data.canDelete ? (
              data.deleted ? (
                <IconInbox
                  className="cursor-pointer opacity-20"
                  tooltip="Restore this message. You can also perform this action by swiping on a message on the left"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleMessageDeletion(data.id, !!data.deleted, reloadConversation)
                  }}
                />
              ) : (
                <IconArchive
                  className="cursor-pointer opacity-20"
                  tooltip="Archive this message. You can also perform this action by swiping on a message on the left"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleMessageDeletion(data.id, !!data.deleted, reloadConversation)
                  }}
                />
              )
            ) : undefined}
            {user.type === 'guardian' &&
              data.type !== 'phone' &&
              data.hasTranslation &&
              data.otherLanguageName === 'English' && (
                <IconFlag
                  className="cursor-pointer opacity-20"
                  tooltip={tr('Flag this translation as incorrect')}
                  onClick={() => {
                    Popups.simpleConfirm(
                      tr('Are you sure you want to notify us that this translation is incorrect?')
                    ).then(() => {
                      makeRequest('/frontend_api/flagTranslation', {
                        id: data.id,
                        text: data.thread.map((t) => t.content).join(', '),
                      })
                        .catch(showAxiosErrorAsToast)
                        .then(() => Toasts.showToast({ message: tr('Thanks for letting us know!') }))
                    })
                  }}
                />
              )}
          </>
        }
      />
    </MessageInfoLauncher>
  )
}

export const ReplyOptionTitle: React.FC<{ replyOption: ReplyOption; className?: string }> = ({
  replyOption,
  className,
}) => {
  const { tr } = useTranslation()
  return (
    <>
      {tr(replyOption.name)}
      {replyOption.alsoToTeachers && (
        <IconShare
          tooltip="This reply can be viewed by all teachers shared on this message."
          size="sm"
          className={className}
        />
      )}
    </>
  )
}

const ConversationFooter: React.FC<{
  data: ThreadConversationData
  replyOption: ReplyOption
  setReplyOption: (replyOption: ReplyOption) => void
  reloadConversation: () => void
  replyOpen: boolean
  setReplyOpen: React.Dispatch<React.SetStateAction<boolean>>
  openReplyAfterTOS: () => void
}> = ({ data, replyOption, setReplyOption, reloadConversation, replyOpen, setReplyOpen, openReplyAfterTOS }) => {
  const { tr } = useTranslation()
  const [response, setResponse] = React.useState<ReplyState>(DEFAULT_REPLY_STATE)
  const [shareOpen, setShareOpen] = React.useState(false)
  return (
    <div className="print-hide z-10 flex flex-col gap-2">
      {shareOpen && (
        <ConversationShare conversation={data} onShare={reloadConversation} onClose={() => setShareOpen(false)} />
      )}
      {replyOpen ? (
        <ConversationReply
          state={response}
          setState={setResponse}
          replyOption={replyOption}
          conversation={data}
          close={() => setReplyOpen(false)}
          onSend={() => {
            reloadConversation()
            setResponse(DEFAULT_REPLY_STATE)
            setReplyOpen(false)
          }}
        />
      ) : (
        <div className="mx-2 -mt-px flex items-center gap-2 border-t border-gray-200 px-2 py-4">
          <TextInput
            value={deltaToText(response.delta)}
            placeholder={tr('Reply')}
            className={`w-0 flex-grow rounded-full ${replyOpen ? 'invisible' : ''}`}
            onFocusChange={(focus) => {
              if (focus) openReplyAfterTOS()
            }}
          />
          <div className="flex">
            <Button
              logo
              shape={data.replyOptions.length > 1 ? ButtonShape.roundedStart : ButtonShape.rounded}
              onClick={openReplyAfterTOS}
            >
              <ReplyOptionTitle replyOption={replyOption} className="ml-2" />
            </Button>
            {data.replyOptions.length > 1 && (
              <Dropdown
                offset={5}
                button={
                  <Button logo shape={ButtonShape.roundedEnd}>
                    <IconCaretUp />
                  </Button>
                }
              >
                {data.replyOptions.map((option) => (
                  <DropdownItem key={option.name} onClick={() => setReplyOption(option)}>
                    <ReplyOptionTitle replyOption={option} className="ml-1 opacity-50" />
                  </DropdownItem>
                ))}
              </Dropdown>
            )}
          </div>
          {data.canShare && (
            <Button logo={data.isShared} shape={ButtonShape.iconCircle} onClick={() => setShareOpen(true)}>
              <IconShare />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export interface ConversationRef {
  tryReload: () => void
}

interface ConversationProps {
  messageID?: number
  messageUserID?: number
  allSchoolMessages: boolean
  showArchived: boolean
  reloadUnreadCounts: () => void
}

export const Conversation = React.forwardRef<ConversationRef, ConversationProps>(
  ({ messageID, messageUserID, allSchoolMessages, showArchived, reloadUnreadCounts }, ref) => {
    const [replyOption, setReplyOption] = React.useState<ReplyOption | undefined>(undefined)
    const [replyOpen, setReplyOpen] = React.useState(false)
    const user = useAppContext().state.user!

    const conversationRef = React.useRef<HTMLDivElement>(null)

    const [translated, setTranslated] = React.useState(false)
    const postData = React.useMemo(() => {
      return { message_id: messageID, message_user_id: messageUserID, translated, allSchoolMessages, showArchived }
    }, [messageID, messageUserID, translated, allSchoolMessages, showArchived])
    const {
      data: conversationData,
      loading,
      reload: reloadConversation,
    } = useRequest<ConversationData>('/frontend_api/getConversation', postData)

    React.useImperativeHandle(ref, () => ({
      tryReload: () => {
        if (!replyOpen) reloadConversation()
      },
    }))

    React.useEffect(() => {
      if (!conversationData) return
      if (conversationData.replyOptions) setReplyOption(conversationData.replyOptions[0])
      if (conversationData.wasRead) reloadUnreadCounts()
      setReplyOpen(false)
    }, [conversationData])

    const requireTermsOfService = useTermsOfService()

    const openReplyAfterTOS = async () => {
      if (await requireTermsOfService(user)) setReplyOpen(true)
    }

    const setAndAddReplyOption = (replyOption: ReplyOption) => {
      if (
        conversationData &&
        conversationData.replyOptions &&
        !conversationData.replyOptions.find((r) => r.type === replyOption.type)
      )
        conversationData.replyOptions.push(replyOption)
      setReplyOption(replyOption)
      openReplyAfterTOS()
    }

    return (
      <div
        ref={conversationRef}
        className="print-full flex w-full flex-grow flex-col bg-white"
        id="tutorial_conversation"
      >
        {loading || conversationData === undefined ? (
          <Loader />
        ) : (
          <>
            {conversationData.type !== 'attendance' && (
              <ConversationHeader
                data={conversationData}
                translated={translated}
                setTranslated={setTranslated}
                reloadConversation={reloadConversation}
                user={user}
              />
            )}
            <AutoScrollToBottom className="flex flex-grow flex-col overflow-auto [&_a]:text-link">
              {conversationData.type === 'text' ? (
                <TextConversation
                  data={conversationData}
                  showArchived={showArchived}
                  setReplyOption={setAndAddReplyOption}
                />
              ) : conversationData.type === 'email' ||
                conversationData.type === 'both' ||
                conversationData.type === 'mass' ||
                conversationData.type === 'system' ? (
                <StandardConversation
                  data={conversationData}
                  showArchived={showArchived}
                  setReplyOption={setAndAddReplyOption}
                />
              ) : conversationData.type === 'phone' ? (
                <PhoneConversation data={conversationData} />
              ) : conversationData.type === 'attendance' ? (
                <AttendanceConversation data={conversationData} />
              ) : (
                <div>Unknown conversation type</div>
              )}
            </AutoScrollToBottom>
            {conversationData.type === 'system' && user.systemAdministrator && (
              <Button
                className="m-2 self-center"
                logo
                small
                link={`/deleteSystemMessage/${conversationData.id}/unconfirmed`}
              >
                Delete This System Message
              </Button>
            )}
            {conversationData.canReply && replyOption && (
              <ConversationFooter
                data={conversationData}
                replyOption={replyOption}
                setReplyOption={setReplyOption}
                reloadConversation={reloadConversation}
                replyOpen={replyOpen}
                setReplyOpen={setReplyOpen}
                openReplyAfterTOS={openReplyAfterTOS}
              />
            )}
          </>
        )}
      </div>
    )
  }
)
